/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import _ from 'lodash'

export interface SelectOption {
  value: any
  label: string
  isDisabled?: boolean
  isFixed?: boolean
  type?: string
  [key: string]: any
}

/**
 * Transform an array of objects into select options format
 *
 * @param arr - Array of objects to transform
 * @param valueKey - Key to use for the option value (default: 'id')
 * @param labelKey - Key to use for the option label (default: 'value')
 * @param sortBy - Optional key to sort by
 * @param preserveOrigVals - If true, preserves all original object properties
 * @returns Array of select options
 */
export function transformToSelectOptions<T = any>(
  arr: T[],
  valueKey: string = 'id',
  labelKey: string = 'value',
  sortBy: string | null = null,
  preserveOrigVals: boolean = false
): SelectOption[] {
  if (!_.isArray(arr)) {
    return []
  }

  let processedArr = arr

  // Sort if requested
  if (sortBy && sortBy.length) {
    processedArr = _.sortBy(arr, (item: any) => {
      const value = item[sortBy]
      if (value && _.isFunction(value.toLowerCase)) {
        return value.toLowerCase()
      }
      return value
    })
  }

  // Transform to select options
  return _.map(processedArr, (val: any) => {
    if (_.isString(val)) {
      return {
        value: val,
        label: val
      }
    }

    return {
      ...(preserveOrigVals ? val : {}),
      value: val[valueKey],
      label: val[labelKey]
    }
  })
}

/**
 * Find option by value
 * @param options - Array of options
 * @param value - Value to find
 */
export function findOptionByValue(options: SelectOption[], value: any): SelectOption | undefined {
  return options.find((opt) => opt.value === value)
}

/**
 * Find multiple options by values
 * @param options - Array of options
 * @param values - Values to find
 */
export function findOptionsByValues(options: SelectOption[], values: any[]): SelectOption[] {
  if (!Array.isArray(values)) {
    return []
  }
  return options.filter((opt) => values.includes(opt.value))
}

/**
 * Extract values from selected options
 * @param options - Selected options
 */
export function extractValues(options: SelectOption | SelectOption[] | null): any {
  if (!options) {
    return null
  }

  if (Array.isArray(options)) {
    return options.map((opt) => opt.value)
  }

  return options.value
}

/**
 * Transform metadata template options to react-select format
 * @param options - Raw options from template definition
 */
export function transformMetadataOptions(options: Array<{ id: string | number; name: string }>): SelectOption[] {
  if (!Array.isArray(options)) {
    return []
  }
  return options.map((opt) => ({
    value: opt.id,
    label: opt.name
  }))
}
